import { collection, doc, writeBatch, serverTimestamp } from "firebase/firestore";

// Position 0 = best. Score is a 1-10 display value derived purely from
// relative position, matching the Beli-style "everything is ranked against
// everything else" model rather than an absolute quality score.
export function computeScoreForPosition(position, totalRanked) {
  if (totalRanked <= 1) return 10;
  const raw = 10 - (position / (totalRanked - 1)) * 9;
  return Math.round(raw * 10) / 10;
}

// Binary-insertion step: given the current sorted (best-first) list and a
// candidate not yet in it, returns the mid index to compare the candidate
// against next, or null if [lo, hi) has collapsed and `lo` is the final
// insertion index.
export function nextComparisonIndex(lo, hi) {
  if (lo >= hi) return null;
  return Math.floor((lo + hi) / 2);
}

// After the user picks a winner for the comparison at `mid`, narrow the
// search range. candidateWon=true means the candidate outranks workingList[mid]
// (so it belongs at an earlier/better index).
export function narrowRange(lo, hi, mid, candidateWon) {
  return candidateWon ? { lo, hi: mid } : { lo: mid + 1, hi };
}

// Where a candidate with an already-known prior score belongs in the current
// best-first active list - used to restore a previously-ranked artist
// without asking the user to re-compare it against everyone from scratch.
//
// Scores are always relative to the current totalRanked, so an archived
// score computed against a different list size can land exactly on today's
// top score even when the candidate really was the outright favorite (e.g.
// unsave the #1 of 5, the remaining 4 immediately reflow so #2 becomes the
// new "10" - resaving the original #1 then ties that same "10"). Ties break
// *before* the tied entry rather than after, so a returning favorite is
// restored to at least its old standing instead of being nudged one spot
// down by the reflow it caused on its way out.
export function insertIndexByScore(activeList, score) {
  let i = 0;
  while (i < activeList.length && activeList[i].score > score) i++;
  return i;
}

// Writes the full ordered ranking list for a user in one batch, recomputing
// position/score for every ACTIVE entry since adding or removing one artist
// shifts everyone else's relative score. Only ever touches active entries -
// archived ones (see archiveRanking) are left alone.
export async function writeRankingBatch(db, uid, orderedActiveArtists) {
  const batch = writeBatch(db);
  const total = orderedActiveArtists.length;
  orderedActiveArtists.forEach((artist, position) => {
    const ref = doc(collection(db, "users", uid, "artistRankings"), artist.artistKey);
    batch.set(
      ref,
      {
        artistKey: artist.artistKey,
        artistName: artist.artistName,
        homeTown: artist.homeTown,
        homeState: artist.homeState,
        active: true,
        position,
        score: computeScoreForPosition(position, total),
        totalRanked: total,
        rankedAt: artist.rankedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
  await batch.commit();
}

// Unsaving a previously-ranked artist archives its ranking doc (active:
// false) instead of deleting it, so its last-known score survives - if the
// artist gets resaved later, that memory lets us restore it automatically
// (see the reactivation path in artists_table.jsx's toggleBookmark) instead
// of re-prompting the user to rank it again from nothing. Position/score are
// left untouched on the archived doc; only the remaining active entries get
// their position/score recomputed.
export async function archiveRanking(db, uid, remainingActiveOrderedArtists, archivedArtistKey) {
  const batch = writeBatch(db);
  const archivedRef = doc(collection(db, "users", uid, "artistRankings"), archivedArtistKey);
  batch.set(archivedRef, { active: false, updatedAt: serverTimestamp() }, { merge: true });
  const total = remainingActiveOrderedArtists.length;
  remainingActiveOrderedArtists.forEach((artist, position) => {
    const ref = doc(collection(db, "users", uid, "artistRankings"), artist.artistKey);
    batch.set(
      ref,
      {
        position,
        score: computeScoreForPosition(position, total),
        totalRanked: total,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
  await batch.commit();
}
